import { alpha, Box, useTheme } from "@mui/material";
import {
  OverlayScrollbarsComponent,
  type OverlayScrollbarsComponentRef,
} from "overlayscrollbars-react";
import type { OverlayScrollbarsComponentProps } from "overlayscrollbars-react";
import React, { type Ref } from "react";

import {
  appBarSlice,
  selectIsAppBarScrolled,
} from "#/features/appBar/model/appBarSlice";
import { useAppDispatch, useAppSelector } from "#/store/hooks";

const assignViewportRef = (
  viewportRef: Ref<HTMLDivElement | null> | undefined,
  viewport: HTMLDivElement | null,
) => {
  if (!viewportRef) {
    return;
  }

  if (typeof viewportRef === "function") {
    viewportRef(viewport);
    return;
  }

  viewportRef.current = viewport;
};

export type PageScrollContainerProps = {
  children: React.ReactNode;
  isPage?: boolean;
  options?: OverlayScrollbarsComponentProps["options"];
  style?: React.CSSProperties;
  onScroll?: (event: Event) => void;
  /** Receives the OverlayScrollbars viewport element when initialized. */
  viewportRef?: Ref<HTMLDivElement | null>;
};

export const PageScrollContainer: React.FC<PageScrollContainerProps> = ({
  children,
  isPage,
  options = {
    scrollbars: {
      autoHide: "scroll",
    },
    overflow: {
      x: "hidden",
    },
  },
  style,
  onScroll,
  viewportRef,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isScrolled = useAppSelector(selectIsAppBarScrolled);

  const overlayScrollbarsRef = (
    instance: OverlayScrollbarsComponentRef<"div"> | null,
  ) => {
    const viewport =
      (instance?.osInstance()?.elements().viewport as
        HTMLDivElement | undefined) ?? null;
    assignViewportRef(viewportRef, viewport);
  };

  return (
    <Box
      sx={{
        display: "contents",
        ".os-theme-dark.os-scrollbar > .os-scrollbar-track > .os-scrollbar-handle":
          {
            background: theme.palette.action.hover,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.light, 0.3),
            },
            "&:active": {
              backgroundColor: alpha(theme.palette.primary.dark, 0.6),
            },
          },
      }}
    >
      <OverlayScrollbarsComponent
        ref={overlayScrollbarsRef}
        defer
        style={style}
        options={options}
        events={
          isPage
            ? {
                scroll: (_, ev) => {
                  if (
                    ev.target instanceof Element &&
                    ev.target.scrollTop > 0 !== isScrolled
                  ) {
                    dispatch(
                      appBarSlice.actions.setIsScrolled(
                        ev.target.scrollTop > 0,
                      ),
                    );
                  }
                  onScroll?.(ev);
                },
                destroyed: () => {
                  dispatch(appBarSlice.actions.setIsScrolled(false));
                },
              }
            : undefined
        }
      >
        {children}
      </OverlayScrollbarsComponent>
    </Box>
  );
};
